import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CONFIDENCE_LABELS,
  api,
  formatMoney,
  plural,
  type Stats,
} from '../api.ts';
import { Icon } from '../components/Icon.tsx';

/**
 * Статистика коллекции (ТЗ 4.1, спринт 5).
 *
 * Числа здесь не украшение: они показывают, чего в каталоге не хватает.
 * Поэтому рядом с «сколько есть» всегда стоит «сколько без фото» и
 * «сколько без места» — со ссылкой, которая сразу отфильтрует список.
 */
export function StatsScreen() {
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    void api.stats().then(setStats);
  }, []);

  return (
    <div className="app">
      <div className="topbar topbar--bordered">
        <button className="icon-btn" onClick={() => navigate(-1)} aria-label="Назад">
          <Icon name="back" className="icon muted" />
        </button>
        <div className="spacer" />
        <h2 className="topbar-title">Статистика</h2>
        <div className="spacer" />
        <span style={{ width: 20 }} />
      </div>

      <div className="scroll">
        {!stats ? (
          <div className="muted small" style={{ marginTop: 'var(--space-4)' }}>
            Загрузка…
          </div>
        ) : stats.specimens === 0 ? (
          <div className="empty">
            <p className="muted small">
              Считать пока нечего. Статистика появится вместе с первым образцом.
            </p>
            <Link className="btn btn--primary" to="/new">
              <Icon name="plus" className="icon icon-sm" /> Добавить образец
            </Link>
          </div>
        ) : (
          <>
            <div className="label-key" style={{ marginTop: 'var(--space-4)' }}>
              Коллекция
            </div>
            <div className="stat-grid">
              <Stat value={stats.specimens} caption={plural(stats.specimens, 'образец', 'образца', 'образцов')} />
              <Stat value={stats.species} caption={plural(stats.species, 'вид', 'вида', 'видов')} />
              <Stat value={stats.countries} caption={plural(stats.countries, 'страна', 'страны', 'стран')} />
              <Stat value={stats.photos} caption={plural(stats.photos, 'снимок', 'снимка', 'снимков')} />
            </div>

            <div className="label-key">Как попали в коллекцию</div>
            <Bars
              rows={[
                { label: 'находки', count: stats.found, href: '/?type=found' },
                { label: 'покупки', count: stats.purchased, href: '/?type=purchased' },
                { label: 'остальное', count: stats.other },
              ].filter((r) => r.count > 0)}
              total={stats.specimens}
            />

            {stats.money && (
              <>
                <div className="label-key">Деньги</div>
                <dl className="rows" style={{ marginTop: 0 }}>
                  {stats.money.purchase.length === 0 && stats.money.valuation.length === 0 && (
                    <div className="row">
                      <dt>Суммы</dt>
                      <dd className="muted">цены ещё не заносились</dd>
                    </div>
                  )}
                  {stats.money.purchase.map((row) => (
                    <div className="row" key={`p-${row.currency}`}>
                      <dt>
                        <Icon name="tag" className="icon icon-sm" />
                        Куплено на
                      </dt>
                      <dd>
                        {formatMoney(row.amount, row.currency)}
                        <div className="hint">
                          {row.count} {plural(row.count, 'образец', 'образца', 'образцов')} с ценой
                        </div>
                      </dd>
                    </div>
                  ))}
                  {stats.money.valuation.map((row) => (
                    <div className="row" key={`v-${row.currency}`}>
                      <dt>
                        <Icon name="tag" className="icon icon-sm" />
                        Оценено в
                      </dt>
                      <dd>
                        {formatMoney(row.amount, row.currency)}
                        <div className="hint">
                          по последним оценкам {row.count}{' '}
                          {plural(row.count, 'образца', 'образцов', 'образцов')}
                        </div>
                      </dd>
                    </div>
                  ))}
                </dl>
                {stats.money.purchase.length > 1 && (
                  <div className="hint">
                    Валюты не складываются между собой: курс на день покупки уже не восстановить
                  </div>
                )}
              </>
            )}

            {stats.prices_hidden && (
              <>
                <div className="label-key">Деньги</div>
                <div className="notice notice--warn">
                  <Icon name="alert" className="icon icon-sm" />
                  <span>
                    Включён режим «без цен» — суммы скрыты. Данные целы, их видно
                    после выключения режима в настройках.
                  </span>
                </div>
              </>
            )}

            <div className="label-key">Что не заполнено</div>
            <dl className="rows" style={{ marginTop: 0 }}>
              <Gap label="без снимка" count={stats.specimens - stats.with_photo} total={stats.specimens} />
              <Gap label="без месторождения" count={stats.specimens - stats.with_locality} total={stats.specimens} />
              <Gap label="без координат" count={stats.specimens - stats.with_coordinates} total={stats.specimens} />
            </dl>

            {stats.by_mineral.length > 0 && (
              <>
                <div className="label-key">Чаще всего</div>
                <Bars
                  rows={stats.by_mineral.slice(0, 8).map((m) => ({
                    label: m.name_ru ?? m.name,
                    count: m.count,
                    href: `/?q=${encodeURIComponent(m.name)}`,
                  }))}
                  total={stats.by_mineral[0]!.count}
                />
              </>
            )}

            {stats.by_country.length > 0 && (
              <>
                <div className="label-key">По странам</div>
                <Bars
                  rows={stats.by_country.map((c) => ({
                    label: c.country,
                    count: c.count,
                    href:
                      c.country === 'без страны'
                        ? undefined
                        : `/?country=${encodeURIComponent(c.country)}`,
                  }))}
                  total={stats.by_country[0]!.count}
                />
              </>
            )}

            <div className="label-key">Достоверность определения</div>
            <Bars
              rows={stats.by_confidence.map((c) => ({
                label: CONFIDENCE_LABELS[c.confidence] ?? c.confidence,
                count: c.count,
              }))}
              total={stats.specimens}
            />

            {stats.by_year.length > 0 && (
              <>
                <div className="label-key">По годам поступления</div>
                <Bars
                  rows={stats.by_year.map((y) => ({ label: String(y.year), count: y.count }))}
                  total={Math.max(...stats.by_year.map((y) => y.count))}
                />
              </>
            )}

            <div className="label-key">Выезды</div>
            <div className="stat-grid">
              <Stat value={stats.trips} caption={plural(stats.trips, 'выезд', 'выезда', 'выездов')} />
              <Stat
                value={stats.with_coordinates}
                caption={`на карте ${plural(stats.with_coordinates, 'точка', 'точки', 'точек')}`}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ value, caption }: { value: number; caption: string }) {
  return (
    <div className="stat">
      <b>{value}</b>
      <span>{caption}</span>
    </div>
  );
}

/** Полоса вместо графика: на телефоне она читается, а легенда — нет. */
function Bars({
  rows,
  total,
}: {
  rows: { label: string; count: number; href?: string }[];
  total: number;
}) {
  const max = Math.max(total, ...rows.map((r) => r.count), 1);
  return (
    <div className="bars">
      {rows.map((row) => {
        const body = (
          <>
            <span className="bar-label">{row.label}</span>
            <span className="bar-track">
              <span className="bar-fill" style={{ width: `${(row.count / max) * 100}%` }} />
            </span>
            <span className="bar-count mono">{row.count}</span>
          </>
        );
        return row.href ? (
          <Link key={row.label} className="bar" to={row.href}>
            {body}
          </Link>
        ) : (
          <div key={row.label} className="bar">
            {body}
          </div>
        );
      })}
    </div>
  );
}

function Gap({ label, count, total }: { label: string; count: number; total: number }) {
  return (
    <div className="row">
      <dt>{label}</dt>
      <dd>
        {count === 0 ? (
          <span className="chip chip--ok">ни одного</span>
        ) : (
          <>
            {count} из {total}
          </>
        )}
      </dd>
    </div>
  );
}
